import {Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output} from '@angular/core';
import {first} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../store";
import {combineLatest} from "rxjs";
import {User} from "../../../user/user";
import {GoogleAnalyticsService} from "../../../shared/services/google-analytics/google-analytics.service";
import {UserService} from "../../../user/user.service";
import {Router} from "@angular/router";
import {UserSubscription} from "../../../user/user-subscription";
import {DbModalService} from "../../../modules/modal/modal";

const PERCENT_OFFER_END_DATE = new Date("2023-11-28 12:00:00").getTime();

@Component({
    selector: 'db-percent-offer',
    templateUrl: './percent-offer.component.html',
    styleUrls: ['./percent-offer.component.scss'],
    //add style for host if hideButton is true
    host: {
        '[class.db-from-login]': 'hideButton'
    },
    standalone: false
})
export class PercentOfferComponent implements OnInit {

  @Output() onTimerEnd = new EventEmitter<void>();
  @Input() endDate: number;
  @Input() blkOffer: boolean = false;
  @Input() demoUser: boolean = false;
  @Input() oneWeekFreeExpired: boolean;
  @Input() oneWeekFreeNotExpired: boolean;
  @Input() paidNotExpired: boolean;
  @Input() hideButton: boolean;

  public user: User;
  public userSubscription: UserSubscription;
  public userLocalStorage: any;
  public isToggled = true;
  public timeLeft = 0;
  public countDownUnit = 'd';
  public percent = 60;
  public earlyBirdDiscount = 20;
  public blkDiscount = 50;

  constructor(
    private readonly $element: ElementRef,
    private readonly _router: Router,
    private readonly _dbModalService: DbModalService,
    private readonly _store: Store<fromStore.DbStoreState>,
    private readonly _googleAnalyticsService: GoogleAnalyticsService,
    private readonly _userService: UserService,
  ) {
  }

  ngOnInit() {
    this.percent = 50;
    this._getUserData();
    this.countDown();
    setInterval(() => this.countDown(), 60000);
  }

  public redirectToPricing(): void {
    const analyticsData = {
      eventCategory: 'Dashboard Action',
      eventAction: 'Top bar button click',
      eventLabel: 'Booster Agency GSO',
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
    const learnMore = '/agency-special-offer';
    window.open(learnMore, "_blank", "noopener");
  }

  public toggleBanner(): void {
    this.isToggled = !this.isToggled;
    !this.isToggled && this.user && localStorage.setItem('PercentOfferBannerHidden' + this.user.id, 'true');
    this.isToggled && this.sendGAEvent('Header button');
  }

  private _getUserData(): void {
    combineLatest(
      this._store.select(fromStore.getUser),
      this._store.select(fromStore.getUserSubscription),
      this._store.select(fromStore.getUserLocalStorage),
    ).pipe(
      first(([user, userSubscription]) => !!user && !!userSubscription)
    )
      .subscribe(([user, userSubscription, userLocalStorage]) => {
        this.user = user;
        this.userSubscription = userSubscription;
        this.userLocalStorage = userLocalStorage;
        this.isToggled = this.user && !localStorage.getItem('PercentOfferBannerHidden' + this.user.id);
      });
  }

  private _offerExpire(): void {
    this.userLocalStorage = {...this.userLocalStorage, percentOfferExpired: 'true'};
    this._userService.setToLocal(this.userLocalStorage)
      .pipe(
        first(response => !!response),
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadUserLocalStorage());
        }
      });
  }

  private countDown(): void {
    const nowDate = Math.floor(Date.now() / 60000) * 60000;
    let personalEndDate = this.endDate;
    if (!personalEndDate) {
      personalEndDate = this.userLocalStorage.blkOfferBannerEndDate || (new Date(nowDate).getTime() + (24 * 60 * 60 * 1000 * 5)); // seven day
    }
    const remainingTime = personalEndDate - nowDate;
   !this.hideButton && this._setToLocalStorage(personalEndDate);
    if (personalEndDate <= 1) {
      this._offerExpire();
      this.onTimerEnd.emit();
      return;
    }
    this.getFormat(remainingTime);
  }

  private _setToLocalStorage(endDate) {
    if (!this.userLocalStorage.blkOfferBannerEndDate) {
      this.userLocalStorage = {...this.userLocalStorage, blkOfferBannerEndDate: endDate};
      this._userService.setToLocal(this.userLocalStorage)
        .pipe(
          first(response => !!response),
        )
        .subscribe(response => {
          if (response.success) {
            this._store.dispatch(new fromStore.LoadUserLocalStorage());
          }
        });
    }
  }


  private getFormat(time: number): void {
    if (time / (60000 * 60 * 24) > 1) {
      this.timeLeft = Math.floor(time / (60000 * 60 * 24));
      this.countDownUnit = 'd';
    } else if (time / (60000 * 60) > 1) {
      this.timeLeft = Math.floor(time / (60000 * 60));
      this.countDownUnit = 'h';
    } else {
      this.timeLeft = Math.floor(time / 60000);
      this.countDownUnit = 'm';
    }
  }

  public claimOffer(): void {
    this.sendGAEvent(!!this.demoUser ? 'Locked button click' : 'Upgrade button click');
    this.isToggled = false;
    this._router.navigate(['/upgrade-plan'])
  }

  public sendGAEvent(eventAction: string): void {
    const event = {
      eventCategory: 'Dashboard Action',
      eventAction,
      eventLabel: !!this.demoUser ? 'AI Builder: Save 50% on annual plans' : 'Speed Booster: Save 60% on annual plans',
    };
    this._googleAnalyticsService.sendAnalyticsEventWithCore(event);
  }

  @HostListener('document:click', ['$event']) onDocumentClick($event: any) {
    if (this.isToggled && !this.$element.nativeElement.contains($event.target.parentElement) && !this.$element.nativeElement.contains($event.target.parentElement.parentElement)) {
      this.toggleBanner();
    }
  }
}
